// Caisse web — calculs et règles de remise, portés à l'identique depuis la
// caisse mobile (pos_clone store/slices/salesSlice.js :
// recalculateFinalizedSaleTotals, isDiscountInValidityPeriod,
// isDiscountAllowedForClientType…) pour que le web et la tablette donnent
// exactement les mêmes totaux. Fonctions pures, sans React ni API.

const DAY_MS = 24 * 60 * 60 * 1000;

// Taux de TVA : 20 → 0.2, 0.2 → 0.2, vide/invalide → 0.
export const parseTaxRate = (raw) => {
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n > 1 ? n / 100 : n;
};

// Les prix sont TTC : TVA = TTC − TTC / (1 + taux).
export const lineTaxFromTTC = (lineTTC, rate) => {
    const r = Number(rate) || 0;
    if (r <= 0 || !Number.isFinite(lineTTC)) return 0;
    return lineTTC - lineTTC / (1 + r);
};

export const isPercentDiscount = (type) => {
    const t = String(type || '').toLowerCase().trim();
    return t === '%' || t === 'pourcentage' || t === 'percentage' || t === 'percent';
};

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Une ligne du panier : {price, quantity, taxRate, discounts: [{value, type}]}.
// Remise % = prix × % × qté ; remise fixe = montant par unité × qté. Le prix
// unitaire remisé ne descend jamais sous 0.
export const computeLine = (line) => {
    const price = Number(line.price) || 0;
    const quantity = Number(line.quantity) || 0;
    let discount = 0;
    (line.discounts || []).forEach((d) => {
        const amount = Number(d.value) || 0;
        discount += isPercentDiscount(d.type)
            ? price * (amount / 100) * quantity
            : amount * quantity;
    });
    const unitNet = quantity > 0 ? Math.max(price - discount / quantity, 0) : 0;
    const total = unitNet * quantity;
    // Remise réellement accordée (plafonnée au prix de la ligne)
    const effectiveDiscount = Math.min(discount, price * quantity);
    const tax = lineTaxFromTTC(total, line.taxRate);
    return {gross: price * quantity, discount: effectiveDiscount, total, tax, ht: total - tax};
};

export const computeCart = (lines) => {
    const acc = {gross: 0, discount: 0, total: 0, tax: 0, ht: 0, count: 0};
    lines.forEach((line) => {
        const r = computeLine(line);
        acc.gross += r.gross;
        acc.discount += r.discount;
        acc.total += r.total;
        acc.tax += r.tax;
        acc.ht += r.ht;
        acc.count += Number(line.quantity) || 0;
    });
    return acc;
};

// ── Règles de la remise (backoffice « Règles de la remise ») ────────────────

// Date serveur ("Y-m-d H:i:s" heure locale, ISO, epoch) → ms, ou null.
export const parseServerDate = (value) => {
    if (!value) return null;
    const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})$/);
    if (parts) {
        const [, y, mo, d, h, mi, s] = parts;
        return new Date(y, mo - 1, d, h, mi, s).getTime();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

// Le jour de fin reste valable jusqu'au minuit suivant.
export const isDiscountInValidityPeriod = (discount, now = Date.now()) => {
    const start = parseServerDate(discount?.startDate);
    const end = parseServerDate(discount?.endDate);
    if (start && now < start) return false;
    if (end && now >= end + DAY_MS) return false;
    return true;
};

export const discountClientTypeIds = (discount) =>
    (discount?.clientTypes || [])
        .filter((ct) => ct && ct.id && ct.active !== false)
        .map((ct) => ct.id);

// Sans restriction : tout le monde, même sans client. Restreinte : il faut un
// client dont le type est dans la liste.
export const isDiscountAllowedForClientType = (discount, clientTypeId) => {
    const allowed = discountClientTypeIds(discount);
    if (allowed.length === 0) return true;
    return !!clientTypeId && allowed.includes(clientTypeId);
};

// Règle stockable / vendable entre la remise et le produit.
export const isDiscountAllowedForProduct = (discount, product) => {
    if (!product) return true;
    const dS = discount?.isStockable !== false;
    const dV = discount?.isVendable !== false;
    const pS = product.isStockable !== false;
    const pV = product.isVendable !== false;
    return (dS && pS) || (dV && pV);
};

export const discountMaxDays = (discount) => {
    const n = Math.floor(Number(discount?.maxDaysSinceLastVisit));
    return Number.isFinite(n) && n > 0 ? n : null;
};

// Pourquoi une remise est refusée (null = applicable). La règle « dernière
// visite » demande l'historique local de la tablette : elle n'est pas
// vérifiable ici, ces remises restent réservées à la caisse mobile.
export const discountRefusal = (discount, {clientTypeId, product, now = Date.now()} = {}) => {
    if (!discount || discount.active === false) return 'Remise inactive';
    if (!isDiscountInValidityPeriod(discount, now)) return "Hors période de validité";
    if (discountMaxDays(discount)) return 'Condition de dernière visite : disponible sur la caisse mobile';
    if (!isDiscountAllowedForClientType(discount, clientTypeId)) return 'Réservée à certains types de client';
    if (!isDiscountAllowedForProduct(discount, product)) return "Ne s'applique pas à ce produit";
    return null;
};

// Change de client : retire des lignes les remises que le nouveau client n'a
// plus le droit d'avoir. Renvoie {lines, removed: [noms]}.
export const revalidateDiscounts = (lines, discountsById, clientTypeId) => {
    const removed = new Set();
    const next = lines.map((line) => {
        const kept = (line.discounts || []).filter((d) => {
            const rule = discountsById.get(d.id);
            const ok = !rule || !discountRefusal(rule, {clientTypeId, product: line.product});
            if (!ok) removed.add(d.name);
            return ok;
        });
        return kept.length === (line.discounts || []).length ? line : {...line, discounts: kept};
    });
    return {lines: next, removed: [...removed]};
};

// ── Encaissement ────────────────────────────────────────────────────────────

// Paiements saisis → montants enregistrés. La monnaie rendue est retirée du
// dernier paiement (en pratique les espèces) pour que la somme enregistrée
// égale le total du ticket. {payments, paid, change, remaining}.
export const settlePayments = (payments, total) => {
    const due = round2(total);
    const paid = round2(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
    const change = paid > due ? round2(paid - due) : 0;
    const remaining = paid < due ? round2(due - paid) : 0;
    let toRemove = change;
    const settled = [...payments]
        .reverse()
        .map((p) => {
            const amount = Number(p.amount) || 0;
            const cut = Math.min(amount, toRemove);
            toRemove = round2(toRemove - cut);
            return {...p, amount: round2(amount - cut)};
        })
        .reverse()
        .filter((p) => p.amount > 0);
    return {payments: settled, paid, change, remaining};
};
